/* Small, additive enhancements layered on top of Dawn's stock
   <header-menu>/<details>/<summary> mega-menu system - nothing here
   replaces native click/keyboard/Escape behavior, which keeps working
   exactly as before. Generic across every top-level menu that has a
   <header-menu> wrapper (today: just Shop By), not hardcoded to one
   menu, so future menus (Brands/Featured/New Arrivals) inherit all of
   it automatically once they get mega-menu content of their own. */

/* Shared by sections 1 and 3 below: forces a mega-menu panel's closed
   look back on for exactly one frame right as it opens (see
   .mega-menu--entering in component-header-mega-content.css), then
   releases it on the next frame. This is what actually makes the
   entrance animate: native <details> reveals its content (display:none
   -> block) synchronously and instantly the moment `open` is set, with
   no rendered "before" frame of its own for a CSS transition to
   interpolate from. Forcing this class on, reading layout (which flushes
   pending style changes so the browser actually paints that state), and
   removing the class next frame gives the transition a real "before" and
   "after" frame to animate between - the same effect `@starting-style`
   provides natively in newer browsers, done manually here for broader
   support. Runs for every mega-menu, regardless of how it opened (click,
   keyboard, or the hover-intent in section 3), since it's driven by the
   generic `toggle` listener in section 1, not attached per trigger. */
function animateMegaMenuEnter(details) {
  var content = details.querySelector('.mega-menu__content');
  if (!content) return;
  details.classList.remove('mega-menu--leaving');
  details.classList.add('mega-menu--entering');
  void content.offsetHeight; // force a reflow - see comment above
  // A single requestAnimationFrame here is a common but unreliable
  // variant of this pattern - its callback can run before the browser
  // has actually painted the "entering" state at all, coalescing the
  // add and remove into one style recalc with nothing to transition
  // from (confirmed empirically: opacity read back as 1 on the very
  // first frame with only one rAF). Nesting a second rAF inside the
  // first is the standard fix - it guarantees a full frame boundary,
  // and therefore a real paint of the "entering" state, has passed
  // before the class is removed.
  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      details.classList.remove('mega-menu--entering');
    });
  });
}

/* 1. Keep the header's existing transparent/blue-scrolled visual system
   in sync with "is any mega menu currently open" - reuses the exact
   class-toggling pattern Dawn's own StickyHeader already uses for scroll
   position (adds/removes a class on .section-header), just driven by a
   different condition. component-header-transparent.css already reads
   this class - see the .mega-menu-open additions there. No new colors,
   no duplicated header-state CSS. This is the fallback that keeps the
   header correctly blue for a menu opened by click or keyboard, with no
   mouse/hover involved at all - independent of the hover-driven states
   below. Also triggers the entrance animation above for every open,
   regardless of source. */
(function () {
  var sectionHeader = document.querySelector('.section-header');
  if (!sectionHeader) return;

  function syncMegaMenuOpenState() {
    var anyOpen = document.querySelector('header-menu details[open]') !== null;
    sectionHeader.classList.toggle('mega-menu-open', anyOpen);
  }

  // Capture phase: the native `toggle` event on <details> doesn't bubble,
  // but does fire during capture, so this one listener catches every
  // open/close on the page regardless of source (click, keyboard, or the
  // hover-intent code below) without needing a listener per menu.
  document.addEventListener(
    'toggle',
    function (event) {
      if (!(event.target instanceof HTMLElement)) return;
      if (!event.target.closest('header-menu')) return;
      syncMegaMenuOpenState();

      var details = event.target;
      if (details.open && details.classList.contains('mega-menu')) {
        animateMegaMenuEnter(details);
      }
    },
    true
  );
})();

/* 2. Immediate header hover-preview: entering the main header bar itself
   (not any one trigger) turns on the blue preview state right away, so
   the whole bar reads as one continuous surface while the pointer moves
   across New Arrivals / Shop By / Sell / Locations / icons - it doesn't
   toggle off and on between items the way a per-trigger listener would.
   .header-wrapper is the exact element component-header-transparent.css
   already keys its transparent/blue rules off, and any open mega-menu
   panel is a DOM descendant of it (position: absolute doesn't remove it
   from the tree) - so a single mouseenter/mouseleave pair here already
   treats "header bar" and "open dropdown beneath it" as one region for
   free, with no extra bookkeeping. This is deliberately separate from
   the per-trigger hover-intent timers below (section 3): this class
   toggles instantly and generically off the header itself; that one
   still separately gates whether any individual menu's dropdown opens. */
(function () {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  var sectionHeader = document.querySelector('.section-header');
  var headerWrapper = document.querySelector('.header-wrapper');
  if (!sectionHeader || !headerWrapper) return;

  // Same breakpoint Dawn's own header already switches to the mobile
  // drawer at (sections/header.liquid) - not a new one.
  var desktopQuery = window.matchMedia('(min-width: 990px)');

  headerWrapper.addEventListener('mouseenter', function () {
    if (!desktopQuery.matches) return;
    sectionHeader.classList.add('header-hover');
  });

  headerWrapper.addEventListener('mouseleave', function () {
    sectionHeader.classList.remove('header-hover');
  });
})();

/* 3. Desktop hover-intent: opens/closes a mega menu on hover, in addition
   to the existing click/keyboard paths, with a short delay each way so
   the menu doesn't flash open on a fast pointer pass-through and doesn't
   snap shut the instant the pointer crosses the gap between the trigger
   and its panel. mouseenter/mouseleave are attached to the whole
   <header-menu> element (trigger + panel together), which is what makes
   "moving from the trigger into the panel" a non-event here - both are
   inside the same element's hover region, so nothing needs to track
   pointer position manually. Purely about whether THIS menu opens -
   the header's own blue state is entirely handled by section 2 above.

   The hover-triggered close also fades the panel out first (see
   animateMegaMenuLeave below) before actually closing it. Click and
   Escape closes are deliberately left instant, not routed through this -
   animating a close the user explicitly commanded (a click, or hitting
   Escape) would mean intercepting native <details> toggling and Dawn's
   own aria-expanded/focus-return handling for those paths, which is a
   materially bigger change than this refinement calls for. Hover-leave
   is the one close path this code already fully owns, so it's the one
   that gets the soft treatment. */
(function () {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  var OPEN_DELAY = 200;
  var CLOSE_DELAY = 250;
  // Keep in sync with .mega-menu--leaving's transition-duration in
  // component-header-mega-content.css (both 200ms) - this timer is what
  // waits for that CSS fade-out to finish before actually closing.
  var CLOSE_ANIM_DURATION = 200;
  var desktopQuery = window.matchMedia('(min-width: 990px)');

  document.querySelectorAll('header-menu').forEach(function (menu) {
    var details = menu.querySelector('details');
    var summary = details && details.querySelector('summary');
    if (!details || !summary) return;

    var timer = null;
    var closeAnimTimer = null;

    function scheduleOpen() {
      if (!desktopQuery.matches) return;
      clearTimeout(timer);
      // Re-entering mid fade-out: cancel the close and let the
      // transition reverse back to fully open from wherever it
      // currently is, rather than letting it finish closing and then
      // immediately reopening (which would look like a restart, not a
      // continuous motion).
      if (details.classList.contains('mega-menu--leaving')) {
        clearTimeout(closeAnimTimer);
        details.classList.remove('mega-menu--leaving');
        return;
      }
      timer = setTimeout(function () {
        if (details.open) return;
        details.open = true;
        summary.setAttribute('aria-expanded', 'true');
      }, OPEN_DELAY);
    }

    function scheduleClose() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        if (!details.open) return;
        animateMegaMenuLeave();
      }, CLOSE_DELAY);
    }

    function animateMegaMenuLeave() {
      details.classList.remove('mega-menu--entering');
      details.classList.add('mega-menu--leaving');
      clearTimeout(closeAnimTimer);
      closeAnimTimer = setTimeout(function () {
        details.classList.remove('mega-menu--leaving');
        details.open = false;
        summary.setAttribute('aria-expanded', 'false');
      }, CLOSE_ANIM_DURATION);
    }

    menu.addEventListener('mouseenter', scheduleOpen);
    menu.addEventListener('mouseleave', scheduleClose);
  });
})();
